import { fmtEur, payIns, type Transaction } from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

const sourceData: Transaction[] = [
  ...payIns,
  // Synthesise a deeper history so the table feels populated.
  ...Array.from({ length: 16 }, (_, i) => {
    const base = payIns[i % payIns.length]!;
    return {
      ...base,
      id: `TX-89${(1832 - i).toString().padStart(4, "0")}`,
      date: i < 8 ? "05 May" : "04 May",
      time: `${(20 - i).toString().padStart(2, "0")}:${(57 - i * 3 + 60) % 60} CET`,
      amountEur: Math.round(base.amountEur * (0.6 + (i % 7) * 0.18)),
      status: i === 5 ? "pending" : i === 11 ? "failed" : "success",
      statusLabel: i === 5 ? "Pending" : i === 11 ? "Failed" : "Settled",
    } as Transaction;
  }),
];

function transactionRow(t: Transaction): string {
  return `<div class="ledger-table-row" style="grid-template-columns: 90px 1.6fr 1fr 1fr 130px 30px;">
    <div class="col-date">
      <strong>${t.date}</strong>
      ${t.time}
    </div>
    <div class="col-name">
      <strong>${t.customer}</strong>
      <span>REF · ${t.id} · ${t.ref}</span>
    </div>
    <div class="col-method">
      <span class="col-method-icon is-${t.method === "sepa" ? "card" : t.method}">${t.method[0]?.toUpperCase()}</span>
      ${t.methodLabel}
    </div>
    <div class="col-amount is-in">
      +${fmtEur(t.amountEur)}
      ${t.amountText ? `<span class="col-amount-sub">${t.amountText}</span>` : ""}
    </div>
    <div class="col-status">
      <span class="status-seal is-${t.status}"></span> ${t.statusLabel}
    </div>
    <div class="col-chevron">→</div>
  </div>`;
}

export function renderPayIns(ctx: Ctx) {
  const total = sourceData.reduce((a, t) => a + t.amountEur, 0);
  const success = sourceData.filter((t) => t.status === "success").length;
  const successRate = ((success / sourceData.length) * 100).toFixed(1);
  const avg = total / sourceData.length;

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Customer Funds<span class="page-eyebrow-rule"></span>Pay-Ins</span>
        <h1 class="page-title">Incoming customer transactions</h1>
      </div>
      <div class="page-head-meta">
        <span class="page-head-date">Reporting Window</span>
        <div class="date-range" id="page-range" role="group">
          <button class="is-active">7d</button>
          <button>30d</button>
          <button>90d</button>
          <button>YTD</button>
        </div>
        <span style="font-size: 0.7rem; color: var(--ink-subtle);">${sourceData.length} pay-ins this window</span>
      </div>
    </section>

    <section class="summary-row">
      <div class="summary-cell">
        <span class="summary-cell-label">Total Volume</span>
        <span class="summary-cell-value">${fmtEur(total, { compact: true })}</span>
        <span class="summary-cell-note is-up">↑ 18.2% vs prev</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Avg Transaction</span>
        <span class="summary-cell-value">${fmtEur(avg)}</span>
        <span class="summary-cell-note">Across ${sourceData.length} pay-ins</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Success Rate</span>
        <span class="summary-cell-value">${successRate}<small style="font-size: 0.5em; color: var(--ink-muted);">%</small></span>
        <span class="summary-cell-note">${success} of ${sourceData.length} settled</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Pending</span>
        <span class="summary-cell-value">${sourceData.filter((t) => t.status === "pending").length}</span>
        <span class="summary-cell-note">Awaiting clearing</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Failed</span>
        <span class="summary-cell-value">${sourceData.filter((t) => t.status === "failed").length}</span>
        <span class="summary-cell-note is-down">Needs review</span>
      </div>
    </section>

    <section class="panel">
      <div class="filter-bar">
        <input class="filter-input" type="search" placeholder="Search customer, reference or amount" id="filter-search" />
        <select class="filter-select" id="filter-method">
          <option value="">All methods</option>
          <option value="wallet">Wallet</option>
          <option value="card">Card / Gateway</option>
          <option value="crypto">Crypto</option>
          <option value="voucher">Voucher</option>
        </select>
        <select class="filter-select" id="filter-status">
          <option value="">All statuses</option>
          <option value="success">Settled</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <select class="filter-select" id="filter-date">
          <option>Today</option>
          <option selected>Last 7d</option>
          <option>Last 30d</option>
        </select>
        <span class="filter-spacer"></span>
        <button class="btn btn-ghost btn-sm">Export ${icon("download", 11)}</button>
      </div>

      <div class="ledger-table" id="pay-ins-table">
        <div class="ledger-table-header" style="grid-template-columns: 90px 1.6fr 1fr 1fr 130px 30px;">
          <span>Date / Time</span>
          <span>Customer / Reference</span>
          <span>Method</span>
          <span style="text-align: right;">Amount</span>
          <span>Status</span>
          <span></span>
        </div>
        <div id="pay-ins-rows"></div>
        <div class="ledger-table-foot">
          <span id="pay-ins-foot">Showing ${sourceData.length} of ${sourceData.length} entries</span>
          <span style="font-size: 0.65rem; color: var(--ink-subtle); letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700;">EUR</span>
        </div>
      </div>
    </section>
  `;

  const rowsEl = ctx.canvas.querySelector<HTMLElement>("#pay-ins-rows")!;
  const footEl = ctx.canvas.querySelector<HTMLElement>("#pay-ins-foot")!;
  const search = ctx.canvas.querySelector<HTMLInputElement>("#filter-search")!;
  const methodSel =
    ctx.canvas.querySelector<HTMLSelectElement>("#filter-method")!;
  const statusSel =
    ctx.canvas.querySelector<HTMLSelectElement>("#filter-status")!;

  function applyFilter() {
    const q = search.value.toLowerCase().trim();
    const m = methodSel.value;
    const s = statusSel.value;
    const filtered = sourceData.filter((t) => {
      if (m && t.method !== m) return false;
      if (s && t.status !== s) return false;
      if (q) {
        const hay =
          `${t.customer} ${t.id} ${t.ref} ${t.amountEur}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rowsEl.innerHTML = filtered.length
      ? filtered.map(transactionRow).join("")
      : `<div style="padding: 3rem; text-align: center; color: var(--ink-muted); font-size: 0.85rem;">No pay-ins match your filters.</div>`;
    footEl.textContent = `Showing ${filtered.length} of ${sourceData.length} entries`;
  }

  search.addEventListener("input", applyFilter);
  methodSel.addEventListener("change", applyFilter);
  statusSel.addEventListener("change", applyFilter);
  applyFilter();
}
